import React from "react";
import { Link } from "react-router-dom";
import { useClassicSiteAuth, login } from "../../auth";
import { redirectToDjangoLoginPage } from "../../auth/authGuards";
import { CLASSIC_SITE_BASE, DJANGO_MEMBER_URLS, SURFACE_STATUS } from "../../classicSite.constants";
import BlockedSurface from "../../components/BlockedSurface";
import BillingBlockedSection from "../../components/BillingBlockedSection";
import LegacyManagedCard from "../../components/LegacyManagedCard";
import MemberIdentityCard from "../../components/MemberIdentityCard";
import CsrfCookieDiagnosticDisplay from "../../components/CsrfCookieDiagnosticDisplay";
import MemberNav from "../../components/MemberNav";
import OwnedListingsPanel from "../../components/OwnedListingsPanel";

function MemberPageShell({ title, children }) {
  return (
    <div className="cs-member">
      <MemberNav />
      {title ? <h1>{title}</h1> : null}
      {children}
    </div>
  );
}

function SessionSummaryCard() {
  const { status, httpStatus, csrfDiagnostic, lastCheckedAt, refresh, isAuthenticated } =
    useClassicSiteAuth();

  return (
    <article className="cs-dashboard-card cs-dashboard-card--session">
      <h3 className="cs-dashboard-card__title">Session</h3>
      <dl className="cs-detail__dl cs-dashboard-card__dl">
        <dt>Status</dt>
        <dd>{status}</dd>
        <dt>HTTP</dt>
        <dd>{httpStatus ?? "—"}</dd>
        <CsrfCookieDiagnosticDisplay
          diagnostic={csrfDiagnostic}
          isAuthenticated={isAuthenticated}
          compact
        />
        <dt>Checked</dt>
        <dd>
          {lastCheckedAt ? new Date(lastCheckedAt).toLocaleString() : "—"}
        </dd>
      </dl>
      <div className="cs-dashboard-card__actions">
        <button type="button" className="cs-btn cs-btn--ghost" onClick={refresh}>
          Refresh session
        </button>
        <Link to={`${CLASSIC_SITE_BASE}/member/auth`} className="cs-btn cs-btn--ghost">
          Session details
        </Link>
      </div>
    </article>
  );
}

/** /classic-site/member — read-only dashboard */
export default function MemberHomePage() {
  return (
    <MemberPageShell title="Member dashboard">
      <p className="cs-member__lead">
        Read-only account view. Billing and listing management remain in legacy
        Django until APIs are verified.
      </p>

      <MemberIdentityCard useAuth />

      <div className="cs-dashboard-grid">
        <SessionSummaryCard />
        <LegacyManagedCard
          title="Billing & subscriptions"
          description="Stripe customer, plans, payment methods — Django only."
          djangoUrl={DJANGO_MEMBER_URLS.profile}
          phaseNote="View blocked cards → Billing section"
        />
        <article className="cs-dashboard-card">
          <h3 className="cs-dashboard-card__title">Quick links</h3>
          <ul className="cs-dashboard-card__links">
            <li>
              <Link to={`${CLASSIC_SITE_BASE}/member/account`}>Account</Link>
            </li>
            <li>
              <Link to={`${CLASSIC_SITE_BASE}/member/listings`}>Listings</Link>
            </li>
            <li>
              <Link to={`${CLASSIC_SITE_BASE}/member/billing`}>Billing</Link>
            </li>
            <li>
              <a href={DJANGO_MEMBER_URLS.profile}>Legacy account overview</a>
            </li>
          </ul>
        </article>
      </div>

      <OwnedListingsPanel compact />
    </MemberPageShell>
  );
}

/** /classic-site/member/account */
export function MemberAccountPage() {
  return (
    <MemberPageShell title="Account">
      <p className="cs-member__lead">
        Identity from <code>hoodies/me/</code>. Billing fields are not available via API.
      </p>
      <MemberIdentityCard useAuth />
      <BlockedSurface
        title="classic_site.Member fields"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl={DJANGO_MEMBER_URLS.profile}
        reason="is_club_member and is_list_member are on classic_site.Member, not exposed on hoodies/me/."
        apiNote="Staff bridge API (admin-tools) is JWT-only — not for member portal."
        specRef="classic-site-react-api-gap-register-v1.md"
      />
      <BillingBlockedSection />
    </MemberPageShell>
  );
}

/** /classic-site/member/billing */
export function MemberBillingPage() {
  return (
    <MemberPageShell title="Billing & subscriptions">
      <p className="cs-member__lead">
        All billing surfaces are legacy-managed. React does not call Stripe or Django billing endpoints.
      </p>
      <BillingBlockedSection />
    </MemberPageShell>
  );
}

/** /classic-site/member/profile — alias for account */
export function MemberProfilePage() {
  return <MemberAccountPage />;
}

export function MemberSubscriptionsPage() {
  return (
    <MemberPageShell title="Subscriptions">
      <BillingBlockedSection />
    </MemberPageShell>
  );
}

export function MemberSubscriptionChoosePage() {
  return (
    <MemberPageShell title="Choose plan">
      <LegacyManagedCard
        title="Choose subscription plan"
        description="Plan selection uses session-stored plan IDs and Stripe plan configuration from Django settings."
        djangoUrl={DJANGO_MEMBER_URLS.subscriptionChoose}
        phaseNote="Phase 5."
      />
    </MemberPageShell>
  );
}

/** /classic-site/member/listings */
export function MemberListingsPage() {
  return (
    <MemberPageShell title="My listings">
      <p className="cs-member__lead">
        Read-only summary only when owner-scoped API exists. Management stays in Django.
      </p>
      <OwnedListingsPanel />
      <div className="cs-dashboard-grid">
        <LegacyManagedCard
          title="Create listing"
          description="Partner listing create flow with form validation and subscription enable rules."
          djangoUrl={DJANGO_MEMBER_URLS.listingsNew}
          phaseNote="Phase 4 — POST API unverified."
        />
        <LegacyManagedCard
          title="Bulk enable listings"
          description="Activate listings up to subscription quantity."
          djangoUrl={DJANGO_MEMBER_URLS.profile}
          phaseNote="Phase 4 — no bulk API."
        />
      </div>
    </MemberPageShell>
  );
}

export function MemberListingNewPage() {
  return (
    <MemberPageShell title="Create listing">
      <LegacyManagedCard
        title="Create listing"
        description="POST /api/hoodie/listings/ unverified for members. Serializer missing recommended_by, administrator, entry_type, enabled logic."
        djangoUrl={DJANGO_MEMBER_URLS.listingsNew}
        phaseNote="Phase 4."
      />
    </MemberPageShell>
  );
}

export function MemberListingDetailPage() {
  return (
    <MemberPageShell title="Listing detail">
      <BlockedSurface
        title="Member listing detail"
        status={SURFACE_STATUS.PARTIAL}
        djangoUrl="/member/listings/:id"
        reason="GET /api/hoodie/listings/{id}/ works for public enabled listings only. Disabled member listings may be absent."
        apiNote={
          <>
            Public preview:{" "}
            <Link to={`${CLASSIC_SITE_BASE}/public/listings`}>public listings</Link>
          </>
        }
        specRef="classic-site-react-member-parity-matrix-v1.md"
      />
    </MemberPageShell>
  );
}

export function MemberAuthPage() {
  const {
    status,
    error,
    httpStatus,
    csrfDiagnostic,
    lastCheckedAt,
    refresh,
    logout,
  } = useClassicSiteAuth();

  return (
    <MemberPageShell title="Session details">
      <p>
        Canonical check: <code>GET /api/hoodie/hoodies/me/</code> with{" "}
        <code>credentials: include</code>.
      </p>

      <dl className="cs-detail__dl">
        <dt>Status</dt>
        <dd>{status}</dd>
        <dt>HTTP status</dt>
        <dd>{httpStatus ?? "—"}</dd>
        <CsrfCookieDiagnosticDisplay diagnostic={csrfDiagnostic} />
        <dt>Last checked</dt>
        <dd>
          {lastCheckedAt ? new Date(lastCheckedAt).toLocaleString() : "—"}
        </dd>
        {error ? (
          <>
            <dt>Message</dt>
            <dd>{error}</dd>
          </>
        ) : null}
      </dl>

      <MemberIdentityCard useAuth />

      <div className="cs-auth-gate__actions">
        <button type="button" className="cs-btn cs-btn--primary" onClick={refresh}>
          Refresh session
        </button>
        <button type="button" className="cs-btn cs-btn--ghost" onClick={() => login()}>
          Sign in
        </button>
        <button
          type="button"
          className="cs-btn cs-btn--ghost"
          onClick={() => redirectToDjangoLoginPage()}
        >
          Django login page
        </button>
        <button type="button" className="cs-btn cs-btn--ghost" onClick={() => logout()}>
          Sign out
        </button>
      </div>
    </MemberPageShell>
  );
}

export function MemberListingEventsPage() {
  return (
    <MemberPageShell title="Listing events">
      <BlockedSurface
        title="Listing events (member editor)"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl="/member/listings/:id/events/"
        reason="Member event list includes inactive/past events. hoodie events API filters active, future, enabled-entry only."
        specRef="classic-site-react-member-parity-matrix-v1.md"
      />
    </MemberPageShell>
  );
}

export function MemberListingPromosPage() {
  return (
    <MemberPageShell title="Listing promos">
      <BlockedSurface
        title="Listing promos (member editor)"
        status={SURFACE_STATUS.BLOCKED}
        djangoUrl="/member/listings/:id/promos/"
        reason="Member promo list includes inactive promos. hoodie promos API returns active only."
        specRef="classic-site-react-member-parity-matrix-v1.md"
      />
    </MemberPageShell>
  );
}
